'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

import type { AuthUser, CentroRole, CentroDefault, AuthContext as AuthContextType } from '@/types/gia'
import { authApi, getAccessToken } from '@/lib/api'
import { getImpersonatedCentroId, setImpersonatedCentroId as setImpersonatedCentroIdStorage } from '@/lib/api/client'

interface AuthContextValue {
  user: AuthUser | null
  centros: CentroRole[]
  centroDefault: CentroDefault | null
  permissions: string[]
  enabledModules: string[]
  impersonatedCentroId: number | null
  isLoading: boolean
  isAuthenticated: boolean
  loginWithGoogle: (credential: string) => Promise<void>
  logout: () => void
  refreshContext: () => Promise<void>
  impersonateCentro: (centroId: number | null) => Promise<void>
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasModule: (module: string) => boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [centros, setCentros] = useState<CentroRole[]>([])
  const [centroDefault, setCentroDefault] = useState<CentroDefault | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [enabledModules, setEnabledModules] = useState<string[]>([])
  const [impersonatedCentroId, setImpersonatedCentroId] = useState<number | null>(() => getImpersonatedCentroId())
  const [isLoading, setIsLoading] = useState(true)

  const clearAuthState = useCallback(() => {
    setUser(null)
    setCentros([])
    setCentroDefault(null)
    setPermissions([])
    setEnabledModules([])
  }, [])

  const loadContext = useCallback(async (centroOverride?: number | null) => {
    try {
      const ctx = await authApi.context({ centroId: centroOverride ?? impersonatedCentroId ?? undefined })

      setUser(ctx.user)
      setCentros(ctx.centros)
      setCentroDefault(ctx.centro_default)
      setPermissions(ctx.permissions)
      setEnabledModules(ctx.enabled_modules)

      return true
    } catch {
      clearAuthState()

      return false
    }
  }, [clearAuthState, impersonatedCentroId])

  const refreshContext = useCallback(async () => {
    await loadContext()
  }, [loadContext])

  const impersonateCentro = useCallback(
    async (centroId: number | null) => {
      setImpersonatedCentroIdStorage(centroId)
      setImpersonatedCentroId(centroId)
      await loadContext(centroId)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('gia:menu-updated'))
      }
    },
    [loadContext]
  )

  useEffect(() => {
    const initAuth = async () => {
      const token = getAccessToken()

      if (token) {
        const success = await loadContext()

        if (!success) {
          const newToken = await authApi.refresh()

          if (newToken) {
            await loadContext()
          }
        }
      }

      setIsLoading(false)
    }

    initAuth()
  }, [loadContext])

  const loginWithGoogle = async (credential: string) => {
    await authApi.googleLogin(credential)
    setImpersonatedCentroIdStorage(null)
    setImpersonatedCentroId(null)
    await loadContext()
  }

  const logout = () => {
    setImpersonatedCentroIdStorage(null)
    setImpersonatedCentroId(null)
    authApi.logout()
    clearAuthState()
  }

  const hasPermission = useCallback(
    (permission: string) => permissions.includes('*') || permissions.includes(permission),
    [permissions]
  )

  const hasAnyPermission = useCallback(
    (perms: string[]) => permissions.includes('*') || perms.some(p => permissions.includes(p)),
    [permissions]
  )

  const hasModule = useCallback((module: string) => enabledModules.includes(module), [enabledModules])

  const value: AuthContextValue = {
    user,
    centros,
    centroDefault,
    permissions,
    enabledModules,
    impersonatedCentroId,
    isLoading,
    isAuthenticated: !!user,
    loginWithGoogle,
    logout,
    refreshContext,
    impersonateCentro,
    hasPermission,
    hasAnyPermission,
    hasModule
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

export default AuthContext
